import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { appBarActionButtonSx } from '../styles/msiAppBarStyles';
import { factorDataUpdateKey, hasFactorDataUpdate } from '../context/factorDataSyncPolicy';
import { uppercaseLabel } from '../styles/typography';
import {
    APP_STYLE_COMPONENT_COLORS as MSI_COMPONENT_COLORS,
    MSI_GEOMETRY,
    APP_STYLE_PALETTE as MSI_PALETTE,
    usesAppStyleGrammar,
    resolveMsiSxRadius,
} from '../styles/msiStyleTokens';
import {
    Alert,
    Badge,
    Box,
    Button,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Typography,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import HistoryIcon from '@mui/icons-material/History';
import { callBackend } from '../utils/api';
import { openNotificationStream } from '../utils/notificationStream';
import PendingImportCard from './PendingImportCard';
import {
    dispatchPortfolioImportApplied,
    portfolioImportPostApplyErrorMessage,
} from '../utils/portfolioImportEvents';
import {
    scopeSecurityOperationEventDetail,
    USER_SETTINGS_OPEN_EVENT,
    WATCHLIST_HISTORY_OPEN_EVENT,
} from '../utils/securityResolutionEvents';
import { WATCHLIST_ACCEPTED_REVISION_SIGNAL_REFRESH_EVENT } from '../utils/watchlistAcceptedRevisionSignal';
import { MARKET_HEALTH_CHANGED_EVENT } from '../utils/marketHealth';
import { watchlistVersionSuffix } from '../utils/watchlistVersionLabels';
import { useSecurityOperationContext } from '../context/SecurityOperationContext';
import {
    isPreviewStaleImportApplyError,
    isWatchlistStaleImportApplyError,
    parseCloudRunError,
    portfolioImportReviewVersionKey,
} from './msiDriftReviewHelpers';

const MSI_NOTIFICATION_AWAITING_PUBLICATION = '__awaitingWatchlistPublication';

const mergeNotificationPage = (current, incoming) => {
    const byId = new Map(current.map((notification) => [notification.id, notification]));
    incoming.forEach((notification) => {
        const previous = byId.get(notification.id);
        const merged = { ...previous, ...notification };
        if (
            previous?.[MSI_NOTIFICATION_AWAITING_PUBLICATION]
            || notification?.[MSI_NOTIFICATION_AWAITING_PUBLICATION]
        ) {
            merged[MSI_NOTIFICATION_AWAITING_PUBLICATION] = true;
        }
        byId.set(notification.id, merged);
    });
    return [...byId.values()]
        .sort((left, right) => Number(right.id) - Number(left.id))
        .slice(0, 50);
};

const sourceLabels = {
    manual_upload: 'Manual',
    ibkr: 'IBKR',
    ibkr_intraday: 'IBKR Intraday',
    file_drop: 'File Drop',
    restore: 'Restored',
    watchlist_sync: 'Watchlist Sync',
    watchlist_editor: 'Watchlist Editor',
};

const typeLabels = {
    import_ready: 'Import ready',
    import_failed: 'Import failed',
    symbol_cache_update: 'Legacy watchlist registration update',
    watchlist_security_resolution: 'Watchlist Upload Results',
    watchlist_upload_result: 'Watchlist Upload Results',
    watchlist_msi_reconciliation: 'Security-master update',
    missing_factor_report: 'Missing factor data update',
    factor_loader_failed: 'Factor loader failed',
    market_health_changed: 'Market health update',
};


const safeMissingFactorSecurityLabel = (value, index) => {
    const text = String(value ?? '').trim();
    if (!text || text.toLowerCase().startsWith('cog_')) {
        return `Security ${index + 1}`;
    }
    return text;
};

const IDENTIFIER_TYPE_LABELS = {
    cusip: 'CUSIP',
    isin: 'ISIN',
    sedol: 'SEDOL',
    ticker: 'Ticker',
    figi: 'FIGI',
};

const normalizeIdentifierType = (raw) => {
    if (raw === null || raw === undefined) return null;
    const text = String(raw).trim().toLowerCase();
    return text || null;
};

const normalizePublicIdentifierType = (raw) => {
    const text = normalizeIdentifierType(raw);
    return text === 'cognive_id' ? null : text;
};

const normalizeIdentifierValue = (raw) => {
    if (raw === null || raw === undefined) return null;
    const text = String(raw).trim();
    return text ? text.toUpperCase() : null;
};

const buildImportApplyErrorMessage = (err) => {
    let detail = String(parseCloudRunError(err).message || '').trim();
    if (!detail) return 'Failed to apply import. Please try again.';
    if (detail.length > 240) {
        detail = `${detail.slice(0, 237)}...`;
    }
    return `Failed to apply import: ${detail}`;
};

const resolveUnregisteredRowIdentifier = (row) => {
    if (!row || typeof row !== 'object') {
        return { label: 'Identifier unavailable', type: null, value: null };
    }
    const inboundType = normalizePublicIdentifierType(row.inbound_id_type);
    const inboundValue = normalizeIdentifierValue(row.inbound_id_value);
    const displayType = normalizePublicIdentifierType(row.display_identifier_type) || inboundType;
    const displayValue = normalizeIdentifierValue(row.display_identifier) || inboundValue;
    const providedLabel = String(row.display_identifier_label || '').trim() || null;
    const ticker = normalizeIdentifierValue(row.ticker);

    if (displayType && displayValue) {
        const label = providedLabel
            || IDENTIFIER_TYPE_LABELS[displayType]
            || displayType.toUpperCase();
        return { label: `${label} ${displayValue}`, type: displayType, typeLabel: label, value: displayValue };
    }
    return { label: 'Identifier unavailable', type: null, typeLabel: null, value: null };
};

const normalizeCountryLabel = (raw) => {
    if (raw === null || raw === undefined) return null;
    const text = String(raw).trim().toUpperCase();
    return text || null;
};

const resolveUnregisteredRowCountry = (row) => {
    if (!row || typeof row !== 'object') return null;
    return (
        normalizeCountryLabel(row.selected_country)
        || normalizeCountryLabel(row.country)
        || normalizeCountryLabel(row.listing_country)
        || normalizeCountryLabel(row.msi_country)
        || normalizeCountryLabel(row.inbound_selected_country)
        || normalizeCountryLabel(row.selected_msi_country)
    );
};

export default function NotificationBell({
    user,
    watchlistPublicationState = null,
    watchlistContextStatus = null,
    onReviewWatchlistContext = null,
}) {
    const { sessionScopeToken } = useSecurityOperationContext();
    const [anchorEl, setAnchorEl] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const latestNotificationIdRef = useRef(0);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [reviewRun, setReviewRun] = useState(null);
    const [reviewLoading, setReviewLoading] = useState(false);
    const [reviewApplying, setReviewApplying] = useState(false);
    const [reviewApplyError, setReviewApplyError] = useState('');
    const watchlistStaleImportReviewKeysRef = useRef(new Set());
    const appliedImportRunIdsRef = useRef(new Set());
    const applyOwner = useMemo(() => ({}), [user?.uid]);
    const applyOwnerRef = useRef(applyOwner);
    applyOwnerRef.current = applyOwner;
    useEffect(() => {
        applyOwnerRef.current = applyOwner;
        setReviewApplying(false);
        setReviewApplyError('');
        setReviewOpen(false);
        setReviewRun(null);
        return () => {
            if (applyOwnerRef.current === applyOwner) applyOwnerRef.current = null;
        };
    }, [applyOwner]);
    const [symbolReviewOpen, setSymbolReviewOpen] = useState(false);
    const [symbolReviewPayload, setSymbolReviewPayload] = useState(null);
    const [symbolUnregisteredSortBy, setSymbolUnregisteredSortBy] = useState('identifierSort');
    const [symbolUnregisteredSortDirection, setSymbolUnregisteredSortDirection] = useState('asc');
    const [securityResolutionReviewOpen, setSecurityResolutionReviewOpen] = useState(false);
    const [securityResolutionReviewPayload, setSecurityResolutionReviewPayload] = useState(null);
    const [missingFactorReviewOpen, setMissingFactorReviewOpen] = useState(false);
    const [missingFactorReviewPayload, setMissingFactorReviewPayload] = useState(null);

    const isSecurityResolutionNotification = useCallback(
        (notif) => (
            notif?.type === 'watchlist_security_resolution'
            || notif?.type === 'watchlist_upload_result'
            || notif?.payload?.notification_kind === 'watchlist_upload_result'
        ),
        [],
    );
    const isMsiReconciliationNotification = useCallback(
        (notif) => (
            notif?.type === 'watchlist_msi_reconciliation'
            || notif?.payload?.notification_kind === 'watchlist_msi_reconciliation'
        ),
        [],
    );
    const isMsiNotificationPublicationCommitted = useCallback((notif) => {
        const payload = notif?.payload || {};
        const revisionId = String(
            payload.watchlist_revision_id || payload.revision_id || '',
        ).trim();
        const revisionSeq = Number(payload.revision_seq);
        return Boolean(
            watchlistPublicationState?.committed
            && revisionId
            && Number.isSafeInteger(revisionSeq)
            && revisionSeq > 0
            && (
                watchlistPublicationState.revisionSeq > revisionSeq
                || (
                    watchlistPublicationState.revisionSeq === revisionSeq
                    && watchlistPublicationState.revisionId === revisionId
                )
            )
        );
    }, [watchlistPublicationState]);
    const displayNotifications = useMemo(() => {
        const hasUnifiedWatchlistResult = notifications.some(isSecurityResolutionNotification);
        const visible = hasUnifiedWatchlistResult
            ? notifications.filter((notif) => notif?.type !== 'symbol_cache_update')
            : notifications;
        return visible.filter((notif) => (
            notif?.[MSI_NOTIFICATION_AWAITING_PUBLICATION] !== true
            || isMsiNotificationPublicationCommitted(notif)
        ));
    }, [
        isMsiNotificationPublicationCommitted,
        isSecurityResolutionNotification,
        notifications,
    ]);
    const unreadCount = useMemo(
        () => displayNotifications.filter((n) => !n.read_at).length,
        [displayNotifications],
    );

    useEffect(() => {
        let active = true;
        setNotifications([]);
        latestNotificationIdRef.current = 0;
        watchlistStaleImportReviewKeysRef.current.clear();
        if (!user?.uid) return undefined;
        const fetchInitial = async () => {
            try {
                const resp = await callBackend('api/notifications?limit=50', 'GET');
                if (!active) return;
                const items = resp?.items || [];
                setNotifications((current) => mergeNotificationPage(current, items));
                const maxId = items.reduce((max, n) => (n?.id && n.id > max ? n.id : max), 0);
                if (maxId > latestNotificationIdRef.current) {
                    latestNotificationIdRef.current = maxId;
                }
            } catch (err) {
                console.error('Failed to fetch notifications:', err);
            }
        };
        void fetchInitial();
        return () => {
            active = false;
        };
    }, [user?.uid]);

    useEffect(() => {
        const type = 'watchlist_context_refresh_available';
        if (!user?.uid || !hasFactorDataUpdate(watchlistContextStatus)) {
            setNotifications((current) => current.filter((item) => item.type !== type));
            return;
        }
        // Only factor dataset changes need a user decision. Internal identity
        // and provider-binding maintenance does not create a notification.
        const key = JSON.stringify([user.uid, factorDataUpdateKey(watchlistContextStatus)]);
        const id = `local:watchlist-context:${key}`;
        setNotifications((current) => {
            if (current.some((item) => item.id === id)) return current;
            return [{
                id,
                type,
                payload: {
                    title: 'New factor data available',
                    message: 'New factor data is ready. Your current data stays active until the update completes.',
                },
                created_at: new Date().toISOString(),
                read_at: null,
            }, ...current.filter((item) => item.type !== type)].slice(0, 50);
        });
    }, [user?.uid, watchlistContextStatus]);

    useEffect(() => {
        if (!user?.uid) return undefined;
        // The stream keeps its own resume cursor for this user, separate from
        // the initial list fetch, and stops when the user changes or unmounts.
        return openNotificationStream({
            onEvent: (event) => {
                if (event.type !== 'notification') return;
                try {
                    const payload = JSON.parse(event.data);
                    const isNew = payload?.id && payload.id > latestNotificationIdRef.current;
                    const awaitsPublication = Boolean(
                        isNew && isMsiReconciliationNotification(payload),
                    );
                    if (awaitsPublication) {
                        window.dispatchEvent(new CustomEvent(
                            WATCHLIST_ACCEPTED_REVISION_SIGNAL_REFRESH_EVENT,
                        ));
                    }
                    setNotifications((prev) => {
                        const notification = awaitsPublication
                            ? {
                                ...payload,
                                [MSI_NOTIFICATION_AWAITING_PUBLICATION]: true,
                            }
                            : payload;
                        return mergeNotificationPage(prev, [notification]);
                    });
                    if (isNew) {
                        latestNotificationIdRef.current = payload.id;
                    }
                    if (isNew && (payload?.type === 'import_ready' || payload?.type === 'import_failed')) {
                        window.dispatchEvent(new CustomEvent('imports-updated'));
                    }
                    if (isNew && payload?.type === 'market_health_changed') {
                        window.dispatchEvent(new CustomEvent(MARKET_HEALTH_CHANGED_EVENT, {
                            detail: { user_uid: user.uid },
                        }));
                        if (payload?.payload?.watchlist_changed === true) {
                            window.dispatchEvent(new CustomEvent(
                                WATCHLIST_ACCEPTED_REVISION_SIGNAL_REFRESH_EVENT,
                            ));
                        }
                    }
                } catch (err) {
                    console.error('Failed to parse notification:', err);
                }
            },
        });
    }, [isMsiReconciliationNotification, user?.uid]);

    const handleOpen = async (event) => {
        setAnchorEl(event.currentTarget);
        const unreadNotifications = displayNotifications.filter((n) => !n.read_at);
        if (unreadNotifications.length === 0) return;
        const unreadPersistedIds = unreadNotifications
            .map((n) => n?.id)
            .filter((id) => Number.isInteger(id));
        const markAllReadLocally = () => {
            const readAt = new Date().toISOString();
            setNotifications((prev) => prev.map((n) => (
                !n.read_at ? { ...n, read_at: readAt } : n
            )));
        };
        if (unreadPersistedIds.length === 0) {
            markAllReadLocally();
            return;
        }
        try {
            await callBackend('api/notifications/mark_read', 'POST', { ids: unreadPersistedIds });
            markAllReadLocally();
        } catch (err) {
            console.error('Failed to mark notifications read:', err);
        }
    };

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const handleReview = async (notification) => {
        if (notification?.type === 'watchlist_context_refresh_available') {
            if (!sessionScopeToken || !onReviewWatchlistContext) return;
            const opened = await onReviewWatchlistContext({
                origin: 'watchlist_context_status',
                sessionScopeToken,
            });
            if (opened === false) return;
            setNotifications((current) => current.map((item) => (
                item.id === notification.id
                    ? { ...item, read_at: new Date().toISOString() }
                    : item
            )));
            handleClose();
            return;
        }
        const payload = notification?.payload || {};
        if (notification?.type === 'market_health_changed') {
            if ((payload.removal_review_required === true || payload.repair_review_required === true)
                && payload.pending_revision_id) {
                handleOpenWatchlistRevision({ watchlist_revision_id: payload.pending_revision_id });
            }
            return;
        }
        if (isMsiReconciliationNotification(notification)) {
            window.dispatchEvent(new CustomEvent(USER_SETTINGS_OPEN_EVENT, {
                detail: {
                    tab: 'msi',
                    focusTarget: 'reconciliation',
                    revisionId: payload?.watchlist_revision_id || payload?.revision_id || null,
                    revisionSeq: payload?.revision_seq || null,
                },
            }));
            handleClose();
            return;
        }
        if (isSecurityResolutionNotification(notification)) {
            setSecurityResolutionReviewPayload(payload);
            setSecurityResolutionReviewOpen(true);
            return;
        }

        const importRunId = payload.import_run_id;
        if (importRunId) {
            setReviewLoading(true);
            setReviewApplyError('');
            try {
                const run = await callBackend(`api/portfolio/imports/${importRunId}`, 'GET');
                setReviewRun(
                    watchlistStaleImportReviewKeysRef.current.has(
                        portfolioImportReviewVersionKey(run),
                    )
                        ? { ...run, is_watchlist_stale: true }
                        : run,
                );
                setReviewOpen(true);
            } catch (err) {
                console.error('Failed to fetch import run:', err);
            } finally {
                setReviewLoading(false);
            }
            return;
        }

        if (notification?.type === 'symbol_cache_update' || payload.job_id) {
            setSymbolReviewPayload(payload);
            setSymbolReviewOpen(true);
            return;
        }

        if (notification?.type === 'missing_factor_report') {
            setMissingFactorReviewPayload(payload);
            setMissingFactorReviewOpen(true);
        }
    };

    const hasReviewAction = (notif) => {
        const payload = notif?.payload || {};
        if (notif?.type === 'market_health_changed') {
            return Boolean(sessionScopeToken
                && (payload.removal_review_required === true || payload.repair_review_required === true)
                && String(payload.pending_revision_id || '').trim());
        }
        if (notif?.type === 'watchlist_context_refresh_available') return Boolean(onReviewWatchlistContext);
        if (isMsiReconciliationNotification(notif)) return true;
        if (isSecurityResolutionNotification(notif)) return true;
        if (payload.import_run_id) return true;
        return notif?.type === 'symbol_cache_update'
            || notif?.type === 'missing_factor_report'
            || Boolean(payload.job_id);
    };

    const symbolSummary = symbolReviewPayload?.summary || {};
    const symbolWatchlistTotal = Number(symbolSummary?.watchlist_total || 0);
    const symbolMappedCount = Number(symbolSummary?.symbols_mapped ?? symbolSummary?.ready_count ?? 0);
    const symbolUnmappedCount = Number(symbolSummary?.symbols_unmapped ?? symbolSummary?.not_ready_count ?? 0);
    const symbolRegistrationTotal = symbolWatchlistTotal || symbolMappedCount + symbolUnmappedCount;
    const symbolUnresolvedRows = Array.isArray(symbolSummary?.unresolved_securities)
        ? symbolSummary.unresolved_securities
        : Array.isArray(symbolSummary?.unmapped_securities)
            ? symbolSummary.unmapped_securities
        : [];
    const symbolUnresolvedTotal = Number(
        symbolSummary?.unresolved_securities_total
            || symbolSummary?.unmapped_securities_total
            || symbolUnmappedCount
            || symbolUnresolvedRows.length,
    );
    const symbolUnresolvedListTruncated = Boolean(
        symbolSummary?.unresolved_securities_truncated || symbolSummary?.unmapped_securities_truncated,
    );
    const symbolUnregisteredRows = useMemo(() => {
        const rows = symbolUnresolvedRows.map((row, index) => {
            const identifier = resolveUnregisteredRowIdentifier(row);
            const country = resolveUnregisteredRowCountry(row) || '';
            const ticker = normalizeIdentifierValue(row?.ticker) || 'Ticker unavailable';
            return {
                key: `${identifier.value || row?.cognive_id || 'unknown'}-${index}`,
                identifier,
                identifierSort: `${identifier.typeLabel || ''} ${identifier.value || ''}`.trim(),
                country,
                ticker,
            };
        });
        const directionMultiplier = symbolUnregisteredSortDirection === 'desc' ? -1 : 1;
        return rows.sort((a, b) => {
            const left = String(a[symbolUnregisteredSortBy] || '').toUpperCase();
            const right = String(b[symbolUnregisteredSortBy] || '').toUpperCase();
            return left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' }) * directionMultiplier;
        });
    }, [symbolUnresolvedRows, symbolUnregisteredSortBy, symbolUnregisteredSortDirection]);
    const handleSymbolUnregisteredSort = (field) => {
        if (symbolUnregisteredSortBy === field) {
            setSymbolUnregisteredSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
            return;
        }
        setSymbolUnregisteredSortBy(field);
        setSymbolUnregisteredSortDirection('asc');
    };
    const rawMissingFactorSecurities = Array.isArray(missingFactorReviewPayload?.affected_securities)
        ? missingFactorReviewPayload.affected_securities
        : [];
    const missingFactorSecurities = rawMissingFactorSecurities.map(safeMissingFactorSecurityLabel);
    const missingFactorSecurityTotal = Number(
        missingFactorReviewPayload?.affected_securities_total
            || missingFactorReviewPayload?.missing_cognive_ids_total
            || (Array.isArray(missingFactorReviewPayload?.missing_cognive_ids)
                ? missingFactorReviewPayload.missing_cognive_ids.length
                : 0)
            || missingFactorSecurities.length
    );
    const missingFactorSecurityListTruncated = Boolean(
        missingFactorReviewPayload?.affected_securities_truncated
            || missingFactorReviewPayload?.missing_cognive_ids_truncated
    );
    const securityResolutionSummary = securityResolutionReviewPayload?.summary || {};
    const securityResolutionTotal = Number(securityResolutionSummary?.total_processed || 0);
    const securityResolutionAttemptedTotal = Number(
        securityResolutionSummary?.attempted_total
        || securityResolutionReviewPayload?.attempted_total
        || securityResolutionTotal
    );
    const securityResolutionActiveCount = Number(
        securityResolutionSummary?.active_watchlist_count || 0
    );
    const securityResolutionNeedsReviewCount = Number(
        securityResolutionSummary?.needs_review_count || 0
    );
    const securityResolutionWaitingMarketDataCount = Number(
        securityResolutionSummary?.waiting_market_data_count || 0
    );
    const securityResolutionMarketDataFailedCount = Number(
        securityResolutionSummary?.market_data_failed_count || 0
    );
    const securityResolutionChangesAppliedCount = Number(
        securityResolutionSummary?.changes_applied_count || 0
    );
    const securityResolutionAutoUpdatedIdentifierCount = Number(
        securityResolutionSummary?.auto_updated_identifier_count
        || securityResolutionSummary?.legacy_resolved_count
        || 0
    );
    const securityResolutionDefaultUs = Number(securityResolutionSummary?.defaulted_to_us_count || 0);
    const securityResolutionDefaultFirst = Number(
        securityResolutionSummary?.defaulted_first_available_country_count || 0
    );
    const securityResolutionCountryFallbackRows = Array.isArray(
        securityResolutionReviewPayload?.country_fallback_rows
    )
        ? securityResolutionReviewPayload.country_fallback_rows.map((row, index) => ({
            key: `${row?.row_index ?? 'fallback'}-${row?.display_identifier ?? index}`,
            identifier: resolveUnregisteredRowIdentifier(row),
            requestedCountry: normalizeCountryLabel(row?.requested_country)
                || String(row?.requested_country || '').trim(),
            resolvedCountry: normalizeCountryLabel(row?.resolved_country)
                || String(row?.resolved_country || '').trim(),
            ticker: normalizeIdentifierValue(row?.ticker) || String(row?.ticker || '').trim() || '-',
        }))
        : [];
    const securityResolutionCountryFallbackTotal = Number(
        securityResolutionReviewPayload?.country_fallback_rows_total
            || securityResolutionSummary?.country_hint_fallback_count
            || securityResolutionCountryFallbackRows.length
    );
    const securityResolutionCountryFallbackTruncated = Boolean(
        securityResolutionReviewPayload?.country_fallback_rows_truncated
    );
    const securityResolutionFailedRows = Array.isArray(
        securityResolutionReviewPayload?.failed_uploaded_rows
    )
        ? securityResolutionReviewPayload.failed_uploaded_rows
        : [];
    const securityResolutionFailedRowsTotal = Number(
        securityResolutionReviewPayload?.failed_uploaded_rows_total
            || securityResolutionSummary?.failed_uploaded_rows_total
            || securityResolutionFailedRows.length
    );
    const securityResolutionFailedRowsTruncated = Boolean(
        securityResolutionReviewPayload?.failed_uploaded_rows_truncated
    );
    const securityResolutionMarketIssueRows = Array.isArray(
        securityResolutionReviewPayload?.market_data_issue_rows
    )
        ? securityResolutionReviewPayload.market_data_issue_rows.map((row, index) => ({
            key: `${row?.inbound_id_value ?? row?.ticker ?? 'market'}-${index}`,
            identifier: resolveUnregisteredRowIdentifier(row),
            country: normalizeCountryLabel(row?.country)
                || normalizeCountryLabel(row?.selected_country)
                || '',
            ticker: normalizeIdentifierValue(row?.ticker) || String(row?.ticker || '').trim() || '-',
            status: String(
                row?.app_readiness_status
                || row?.market_registration_status
                || ''
            ).trim().toLowerCase(),
        }))
        : [];
    const securityResolutionMarketIssueRowsTotal = Number(
        securityResolutionReviewPayload?.market_data_issue_rows_total
            || securityResolutionMarketIssueRows.length
    );
    const securityResolutionMarketIssueRowsTruncated = Boolean(
        securityResolutionReviewPayload?.market_data_issue_rows_truncated
    );
    const securityResolutionDisposition = String(
        securityResolutionReviewPayload?.upload_disposition
            || securityResolutionSummary?.upload_disposition
            || (securityResolutionFailedRowsTotal > 0 ? 'partial' : 'completed')
    );
    const securityResolutionFailureCategoryLabels = {
        invalid_input: 'Invalid input',
        unresolved: 'Unresolved',
        ambiguous: 'Needs country review',
        ambiguous_within_watchlist: 'Conflicts with watchlist',
    };
    const securityResolutionHasActionableFallback = (
        securityResolutionCountryFallbackTotal > 0
        || securityResolutionDefaultUs > 0
        || securityResolutionDefaultFirst > 0
    );
    const securityResolutionIntroText = String(
        securityResolutionReviewPayload?.message || ''
    ).trim() || (
        securityResolutionAttemptedTotal > 0
            ? `${securityResolutionActiveCount} of ${securityResolutionAttemptedTotal} uploaded securit${securityResolutionAttemptedTotal === 1 ? 'y is' : 'ies are'} active in your Watchlist.`
            : 'Watchlist upload results are available.'
    );
    const securityResolutionActionText = (() => {
        if (securityResolutionFailedRowsTotal > 0) {
            return securityResolutionDisposition === 'rejected'
                ? `${securityResolutionFailedRowsTotal} uploaded row${securityResolutionFailedRowsTotal === 1 ? '' : 's'} blocked the upload. Review the list below.`
                : `${securityResolutionFailedRowsTotal} uploaded row${securityResolutionFailedRowsTotal === 1 ? '' : 's'} need attention. Review the list below.`;
        }
        if (securityResolutionMarketDataFailedCount > 0) {
            return 'Open User Settings → Security Resolution to review market-data registration issues.';
        }
        if (securityResolutionWaitingMarketDataCount > 0) {
            return 'No action is needed while market data registration finishes.';
        }
        if (securityResolutionCountryFallbackTotal > 0) {
            return `${securityResolutionCountryFallbackTotal} securit${securityResolutionCountryFallbackTotal === 1 ? 'y used' : 'ies used'} a resolved country because the uploaded country was not available.`;
        }
        if (securityResolutionDefaultUs > 0 || securityResolutionDefaultFirst > 0) {
            return 'Some securities used default country choices. Review them in User Settings if the country looks wrong.';
        }
        return 'No action needed.';
    })();
    const securityResolutionIdentityResolvedCount = Math.max(
        0,
        securityResolutionAttemptedTotal - securityResolutionFailedRowsTotal,
    );
    const securityResolutionDisplayedNeedsReviewCount = Math.max(
        securityResolutionNeedsReviewCount,
        securityResolutionFailedRowsTotal + securityResolutionMarketDataFailedCount,
    );
    const securityResolutionSummaryRows = [
        { label: 'Uploaded', value: securityResolutionAttemptedTotal, always: true },
        {
            label: 'Identity resolved',
            value: securityResolutionIdentityResolvedCount,
            always: securityResolutionAttemptedTotal > 0,
        },
        { label: 'Needs review', value: securityResolutionDisplayedNeedsReviewCount, severity: 'warning' },
        { label: 'Waiting market data', value: securityResolutionWaitingMarketDataCount, severity: 'info' },
        { label: 'Market data failed', value: securityResolutionMarketDataFailedCount, severity: 'error' },
        { label: 'Updated', value: securityResolutionChangesAppliedCount, severity: 'success' },
        {
            label: 'Auto-updated identifiers',
            value: securityResolutionAutoUpdatedIdentifierCount,
        },
        {
            label: 'Used resolved country',
            value: securityResolutionCountryFallbackTotal,
            severity: 'warning',
        },
    ].filter((row) => row.always || Number(row.value || 0) > 0);

    const handleApply = async (run) => {
        const isCurrentOwner = () => applyOwnerRef.current === applyOwner;
        const appliedKey = `${user?.uid}:${run?.id}`;
        const runId = run?.id == null ? '' : String(run.id);
        const reviewKey = portfolioImportReviewVersionKey(run);
        if (
            !runId
            || !reviewKey
            || reviewApplying
            || appliedImportRunIdsRef.current.has(appliedKey)
            || run?.is_watchlist_stale === true
            || watchlistStaleImportReviewKeysRef.current.has(reviewKey)
        ) return;
        setReviewApplying(true);
        setReviewApplyError('');
        try {
            if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
                await new Promise((resolve) => window.requestAnimationFrame(resolve));
                if (!isCurrentOwner()) return;
            }
            await callBackend(`api/portfolio/imports/${run.id}/apply`, 'POST', {
                expected_staged_state_hash: run?.source_metadata_json?.staged_state_hash ?? null,
            });
            if (!isCurrentOwner()) return;
            appliedImportRunIdsRef.current.add(appliedKey);
            setReviewRun({ ...run, status: 'applied' });
            await dispatchPortfolioImportApplied({ userUid: user?.uid });
            if (!isCurrentOwner()) return;
            window.dispatchEvent(new CustomEvent('imports-updated'));
            setReviewOpen(false);
        } catch (err) {
            if (!isCurrentOwner()) return;
            console.error('Failed to apply import:', err);
            if (appliedImportRunIdsRef.current.has(appliedKey)) {
                setReviewApplyError(portfolioImportPostApplyErrorMessage(err));
                window.dispatchEvent(new CustomEvent('imports-updated'));
                return;
            }
            setReviewApplyError(buildImportApplyErrorMessage(err));
            const parsedError = parseCloudRunError(err);
            if (isWatchlistStaleImportApplyError(parsedError)) {
                watchlistStaleImportReviewKeysRef.current.add(reviewKey);
                setReviewRun((current) => (
                    portfolioImportReviewVersionKey(current) === reviewKey
                        ? { ...current, is_watchlist_stale: true }
                        : current
                ));
                window.dispatchEvent(new CustomEvent('imports-updated'));
                try {
                    const refreshedRun = await callBackend(`api/portfolio/imports/${run.id}`, 'GET');
                    if (!isCurrentOwner()) return;
                    setReviewRun((current) => (
                        String(current?.id ?? '') === runId
                            ? (
                                watchlistStaleImportReviewKeysRef.current.has(
                                    portfolioImportReviewVersionKey(refreshedRun),
                                )
                                    ? { ...(refreshedRun || current), is_watchlist_stale: true }
                                    : (refreshedRun || current)
                            )
                            : current
                    ));
                } catch (_refreshError) {
                    // The structured conflict is authoritative enough to disable retry locally.
                }
            } else if (isPreviewStaleImportApplyError(parsedError)) {
                try {
                    const refreshedRun = await callBackend(
                        `api/portfolio/imports/${run.id}`,
                        'GET',
                    );
                    if (!isCurrentOwner()) return;
                    setReviewRun((current) => (
                        String(current?.id ?? '') === runId
                            ? (refreshedRun || current)
                            : current
                    ));
                } catch (_refreshError) {
                    // Keep the stale token so another Apply remains fail-closed.
                }
            }
        } finally {
            if (isCurrentOwner()) setReviewApplying(false);
        }
    };

    const handleIgnore = async (run) => {
        if (!run?.id) return;
        try {
            await callBackend(`api/portfolio/imports/${run.id}/ignore`, 'POST');
            setReviewOpen(false);
            window.dispatchEvent(new CustomEvent('imports-updated'));
        } catch (err) {
            console.error('Failed to ignore import:', err);
        }
    };

    const handleOpenWatchlistRevision = (payload) => {
        const storedRevisionId = payload?.watchlist_revision_id == null
            ? ''
            : String(payload.watchlist_revision_id).trim();
        // A factor-only refresh is a note on a version; open that version.
        const versionRevisionId = String(payload?.version_revision_id || '').trim();
        const revisionId = versionRevisionId || storedRevisionId;
        if (!revisionId || typeof window === 'undefined') return;
        const detail = scopeSecurityOperationEventDetail({
            revisionId,
            revisionSeq: revisionId === storedRevisionId ? payload?.revision_seq ?? null : null,
        }, sessionScopeToken);
        if (!detail) return;
        window.dispatchEvent(new CustomEvent(WATCHLIST_HISTORY_OPEN_EVENT, {
            detail,
        }));
        setSecurityResolutionReviewOpen(false);
        handleClose();
    };

    return (
        <>
            <IconButton
                color="inherit"
                onClick={handleOpen}
                aria-label="Notifications"
                sx={appBarActionButtonSx}
            >
                <Badge
                    color="error"
                    badgeContent={unreadCount}
                    invisible={unreadCount === 0}
                    sx={{
                        '& .MuiBadge-badge': {
                            minWidth: 16,
                            height: 16,
                            fontSize: '0.58rem',
                            fontWeight: 700,
                        },
                    }}
                >
                    <NotificationsNoneOutlinedIcon sx={{ fontSize: 18 }} />
                </Badge>
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        width: 350,
                        maxHeight: 420,
                        mt: 1,
                        borderRadius: (theme) => resolveMsiSxRadius(
                            theme,
                            MSI_GEOMETRY.radii.panel,
                            3,
                        ),
                        border: '1px solid',
                        borderColor: (theme) => (usesAppStyleGrammar(theme)
                            ? MSI_PALETTE.line
                            : alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.34 : 0.62)),
                        backgroundColor: (theme) => (
                            usesAppStyleGrammar(theme) ? MSI_PALETTE.surface : theme.palette.background.paper
                        ),
                        backgroundImage: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
                        boxShadow: (theme) => (usesAppStyleGrammar(theme)
                            ? '0 8px 24px rgba(12, 23, 38, 0.12)'
                            : (theme.palette.mode === 'dark'
                                ? '0 20px 38px rgba(0, 0, 0, 0.4)'
                                : '0 18px 36px rgba(15, 23, 42, 0.12)')),
                    },
                }}
            >
                <Box sx={{ px: 2, py: 1.15 }}>
                    <Typography
                        variant="caption"
                        sx={uppercaseLabel({
                            display: 'block',
                            mb: 0.4,
                            letterSpacing: (theme) => (usesAppStyleGrammar(theme) ? 0 : '0.12em'),
                            color: 'text.secondary',
                        })}
                    >
                        Activity
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Notifications
                    </Typography>
                </Box>
                <Divider />
                {displayNotifications.length === 0 && (
                    <MenuItem disabled>
                        <Typography variant="body2">No notifications yet.</Typography>
                    </MenuItem>
                )}
                {displayNotifications.map((notif, idx) => {
                    const payload = notif.payload || {};
                    const sourceLabel = sourceLabels[payload.source] || payload.source || 'Import';
                    return (
                        <Box key={notif.id}>
                            <MenuItem
                                sx={{
                                    alignItems: 'flex-start',
                                    whiteSpace: 'normal',
                                    py: 1.1,
                                    px: 1.5,
                                }}
                                onClick={() => {
                                    handleReview(notif);
                                }}
                            >
                                <Box>
                                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', fontWeight: 700 }}>
                                            {payload.title || typeLabels[notif.type] || 'Notification'}
                                        </Typography>
                                        {payload.source && (
                                            <Chip
                                                size="small"
                                                label={sourceLabel}
                                                variant="outlined"
                                                sx={{
                                                    height: 20,
                                                    '& .MuiChip-label': {
                                                        px: 0.8,
                                                        fontSize: '0.62rem',
                                                        fontWeight: 600,
                                                        letterSpacing: 0,
                                                    },
                                                }}
                                            />
                                        )}
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.76rem', lineHeight: 1.45 }}>
                                        {payload.message || ''}
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                        <Typography variant="caption" color="text.secondary">
                                            {notif.created_at ? new Date(notif.created_at).toLocaleString() : ''}
                                        </Typography>
                                        {hasReviewAction(notif) && (
                                            <Button
                                                size="small"
                                                variant="text"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleReview(notif);
                                                }}
                                                sx={{
                                                    minHeight: 24,
                                                    px: 0.75,
                                                    borderRadius: `${MSI_GEOMETRY.radii.button}px`,
                                                    ...uppercaseLabel({
                                                        fontSize: '0.66rem',
                                                        letterSpacing: 0,
                                                    }),
                                                }}
                                            >
                                                Review
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            </MenuItem>
                            {idx !== displayNotifications.length - 1 && <Divider />}
                        </Box>
                    );
                })}
            </Menu>

            <Dialog open={reviewOpen} onClose={() => setReviewOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Import Review</DialogTitle>
                <DialogContent dividers>
                    {reviewApplyError && (
                        <Alert severity="error" sx={{ mb: 1.5 }}>
                            {reviewApplyError}
                        </Alert>
                    )}
                    {reviewRun ? (
                        reviewRun.status === 'failed' ? (
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Typography variant="body2" color="error">
                                    Import failed: See 'Recent Imports' for details.
                                </Typography>
                                {reviewRun.error_message && (
                                    <Typography variant="body2" color="text.secondary">
                                        {reviewRun.error_message}
                                    </Typography>
                                )}
                            </Box>
                        ) : (
                            <PendingImportCard
                                importRun={reviewRun}
                                onApply={reviewRun.status === 'pending_apply' ? handleApply : undefined}
                                onIgnore={
                                    reviewRun.status === 'pending_apply' && reviewRun.source !== 'watchlist_sync'
                                        ? handleIgnore
                                        : undefined
                                }
                                applying={reviewApplying}
                                showActions={reviewRun.status === 'pending_apply'}
                            />
                        )
                    ) : (
                        <Typography variant="body2">
                            {reviewLoading ? 'Loading import…' : 'Import not available.'}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setReviewOpen(false)}
                        disabled={reviewApplying}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={symbolReviewOpen}
                onClose={() => setSymbolReviewOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Legacy Watchlist Registration Details</DialogTitle>
                <DialogContent dividers>
                    {symbolReviewPayload ? (
                        <Box display="flex" flexDirection="column" gap={1.5}>
                            <Typography variant="body2" color="text.secondary">
                                {symbolRegistrationTotal > 0
                                    ? `${symbolMappedCount} of ${symbolRegistrationTotal} watchlist securities were registered and will appear in the active Watchlist.`
                                    : 'Legacy watchlist registration details are available below.'}
                            </Typography>
                            {symbolReviewPayload?.error && (
                                <Typography variant="body2" color="error">
                                    {symbolReviewPayload.error}
                                </Typography>
                            )}
                            <Box display="flex" gap={1} flexWrap="wrap">
                                <Chip size="small" label={`In Watchlist: ${symbolWatchlistTotal}`} variant="outlined" />
                                <Chip size="small" label={`Registered: ${symbolMappedCount}`} color={symbolUnmappedCount > 0 ? 'warning' : 'success'} variant="outlined" />
                                {symbolUnmappedCount > 0 && (
                                    <Chip size="small" label={`Not Registered: ${symbolUnmappedCount}`} color="error" variant="outlined" />
                                )}
                            </Box>
                            {symbolUnmappedCount > 0 ? (
                                <Typography variant="body2" color="warning.main">
                                    {symbolUnmappedCount} securit{symbolUnmappedCount === 1 ? 'y' : 'ies'} could not be registered. Failed rows will not appear in the active Watchlist; review them in User Settings → Security Resolution.
                                </Typography>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    All watchlist securities were registered and will appear in the active Watchlist.
                                </Typography>
                            )}
                            {symbolUnresolvedTotal > 0 && symbolUnresolvedRows.length > 0 ? (
                                <Box>
                                    <TableContainer
                                        sx={{
                                            maxHeight: 280,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Table size="small" stickyHeader aria-label="Not registered securities">
                                            <TableHead>
                                                <TableRow>
                                                    {[
                                                        ['identifierSort', 'Identifier'],
                                                        ['country', 'Country'],
                                                        ['ticker', 'Ticker'],
                                                    ].map(([field, label]) => (
                                                        <TableCell key={field} sortDirection={
                                                            symbolUnregisteredSortBy === field
                                                                ? symbolUnregisteredSortDirection
                                                                : false
                                                        }>
                                                            <TableSortLabel
                                                                active={symbolUnregisteredSortBy === field}
                                                                direction={
                                                                    symbolUnregisteredSortBy === field
                                                                        ? symbolUnregisteredSortDirection
                                                                        : 'asc'
                                                                }
                                                                onClick={() => handleSymbolUnregisteredSort(field)}
                                                            >
                                                                {label}
                                                            </TableSortLabel>
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {symbolUnregisteredRows.map((row) => (
                                                    <TableRow key={row.key} hover>
                                                        <TableCell>
                                                            {row.identifier.value ? (
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                                                    <Chip
                                                                        size="small"
                                                                        variant="outlined"
                                                                        label={row.identifier.typeLabel}
                                                                        sx={{
                                                                            height: 20,
                                                                            fontSize: '0.64rem',
                                                                            fontWeight: 700,
                                                                            letterSpacing: '0.04em',
                                                                        }}
                                                                    />
                                                                    <Box
                                                                        component="span"
                                                                        sx={{
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap',
                                                                            fontFamily: 'monospace',
                                                                            fontSize: '0.8rem',
                                                                        }}
                                                                    >
                                                                        {row.identifier.value}
                                                                    </Box>
                                                                </Box>
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Identifier unavailable
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                                {row.country || '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                                                {row.ticker}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    {symbolUnresolvedListTruncated && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                            Showing a truncated list. Additional unresolved securities are not shown.
                                        </Typography>
                                    )}
                                </Box>
                            ) : symbolUnresolvedTotal > 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    Detailed not-registered security rows were not included with this notification.
                                </Typography>
                            ) : (
                                <Typography variant="body2">
                                    This legacy notification does not include not-registered rows.
                                </Typography>
                            )}
                        </Box>
                    ) : (
                        <Typography variant="body2">Legacy registration details are not available.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSymbolReviewOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={securityResolutionReviewOpen}
                onClose={() => setSecurityResolutionReviewOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {securityResolutionReviewPayload?.title || 'Watchlist Upload Results'}
                </DialogTitle>
                <DialogContent dividers>
                    {securityResolutionReviewPayload ? (
                        <Box display="flex" flexDirection="column" gap={1.5}>
                            <Box display="flex" flexDirection="column" gap={0.5}>
                                <Typography variant="body2" color="text.primary">
                                    {securityResolutionIntroText}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color={
                                        securityResolutionFailedRowsTotal > 0
                                            || securityResolutionMarketDataFailedCount > 0
                                            || securityResolutionHasActionableFallback
                                            ? 'warning.main'
                                            : 'text.secondary'
                                    }
                                >
                                    {securityResolutionActionText}
                                </Typography>
                            </Box>
                            {securityResolutionReviewPayload?.watchlist_revision_id && (
                                <Box>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<HistoryIcon fontSize="small" />}
                                        onClick={() => handleOpenWatchlistRevision(securityResolutionReviewPayload)}
                                        sx={{ textTransform: 'none', fontWeight: 700 }}
                                    >
                                        View changes
                                        {watchlistVersionSuffix(securityResolutionReviewPayload)}
                                    </Button>
                                </Box>
                            )}
                            <TableContainer
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1.5,
                                }}
                            >
                                <Table size="small" aria-label="Watchlist upload result summary">
                                    <TableBody>
                                        {securityResolutionSummaryRows.map((row) => (
                                            <TableRow key={row.label}>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                    sx={{ py: 0.65, color: 'text.secondary' }}
                                                >
                                                    {row.label}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        py: 0.65,
                                                        fontWeight: 700,
                                                        color: row.severity ? `${row.severity}.main` : 'text.primary',
                                                    }}
                                                >
                                                    {row.value}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {securityResolutionMarketIssueRows.length > 0 && (
                                <Box display="flex" flexDirection="column" gap={0.75} mt={0.5}>
                                    <Typography variant="subtitle2" sx={{ fontSize: '0.78rem' }}>
                                        Market data registration rows:
                                    </Typography>
                                    <TableContainer
                                        sx={{
                                            maxHeight: 260,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Table size="small" stickyHeader aria-label="Market data registration rows">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Identifier</TableCell>
                                                    <TableCell>Country</TableCell>
                                                    <TableCell>Ticker</TableCell>
                                                    <TableCell>Status</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {securityResolutionMarketIssueRows.map((row) => (
                                                    <TableRow key={row.key} hover>
                                                        <TableCell>
                                                            {row.identifier.value ? (
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                                                    <Chip
                                                                        size="small"
                                                                        variant="outlined"
                                                                        label={row.identifier.typeLabel}
                                                                        sx={{
                                                                            height: 20,
                                                                            fontSize: '0.64rem',
                                                                            fontWeight: 700,
                                                                            letterSpacing: '0.04em',
                                                                        }}
                                                                    />
                                                                    <Box
                                                                        component="span"
                                                                        sx={{
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap',
                                                                            fontFamily: 'monospace',
                                                                            fontSize: '0.8rem',
                                                                        }}
                                                                    >
                                                                        {row.identifier.value}
                                                                    </Box>
                                                                </Box>
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Identifier unavailable
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                                {row.country || '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                                                {row.ticker}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {row.status === 'market_registration_failed'
                                                                    ? 'Unrecognized'
                                                                    : 'Waiting'}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    {(securityResolutionMarketIssueRowsTruncated
                                        || securityResolutionMarketIssueRowsTotal
                                            > securityResolutionMarketIssueRows.length) && (
                                        <Typography variant="caption" color="text.secondary">
                                            {`Showing ${securityResolutionMarketIssueRows.length} of `
                                                + `${securityResolutionMarketIssueRowsTotal}.`}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            {securityResolutionCountryFallbackRows.length > 0 && (
                                <Box display="flex" flexDirection="column" gap={0.75} mt={0.5}>
                                    <Typography variant="subtitle2" sx={{ fontSize: '0.78rem' }}>
                                        Uploaded country not available; resolved country used:
                                    </Typography>
                                    <TableContainer
                                        sx={{
                                            maxHeight: 260,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Table size="small" stickyHeader aria-label="Requested country fallbacks">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Identifier</TableCell>
                                                    <TableCell>Requested Country</TableCell>
                                                    <TableCell>Resolved Country</TableCell>
                                                    <TableCell>Ticker</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {securityResolutionCountryFallbackRows.map((row) => (
                                                    <TableRow key={row.key} hover>
                                                        <TableCell>
                                                            {row.identifier.value ? (
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                                                    <Chip
                                                                        size="small"
                                                                        variant="outlined"
                                                                        label={row.identifier.typeLabel}
                                                                        sx={{
                                                                            height: 20,
                                                                            fontSize: '0.64rem',
                                                                            fontWeight: 700,
                                                                            letterSpacing: '0.04em',
                                                                        }}
                                                                    />
                                                                    <Box
                                                                        component="span"
                                                                        sx={{
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap',
                                                                            fontFamily: 'monospace',
                                                                            fontSize: '0.8rem',
                                                                        }}
                                                                    >
                                                                        {row.identifier.value}
                                                                    </Box>
                                                                </Box>
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Identifier unavailable
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                                {row.requestedCountry || '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                                {row.resolvedCountry || '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                                                {row.ticker}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    {(securityResolutionCountryFallbackTruncated
                                        || securityResolutionCountryFallbackTotal
                                            > securityResolutionCountryFallbackRows.length) && (
                                        <Typography variant="caption" color="text.secondary">
                                            {`Showing ${securityResolutionCountryFallbackRows.length} of `
                                                + `${securityResolutionCountryFallbackTotal}.`}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            {securityResolutionFailedRowsTotal > 0 && (
                                <Box display="flex" flexDirection="column" gap={0.5} mt={0.5}>
                                    <Typography variant="subtitle2" sx={{ fontSize: '0.78rem' }}>
                                        {securityResolutionDisposition === 'rejected'
                                            ? 'Upload paused. These uploaded rows blocked resolution:'
                                            : `Uploaded rows that need attention (${securityResolutionFailedRowsTotal}):`}
                                    </Typography>
                                    <Box
                                        component="ul"
                                        sx={{
                                            m: 0,
                                            pl: 2.5,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.35,
                                        }}
                                    >
                                        {securityResolutionFailedRows.map((row, index) => {
                                            const identifierLabel = row?.identifier_label
                                                || row?.identifier_value
                                                || `Row ${row?.row_index ?? index + 1}`;
                                            const tickerText = String(row?.ticker || '').trim();
                                            const label = tickerText
                                                ? `${tickerText} — ${identifierLabel}`
                                                : identifierLabel;
                                            const categoryKey = String(row?.category || 'unresolved');
                                            const categoryLabel =
                                                securityResolutionFailureCategoryLabels[categoryKey]
                                                || 'Needs review';
                                            const key = `${row?.row_index ?? 'r'}-${row?.identifier_value ?? index}`;
                                            return (
                                                <Box
                                                    key={key}
                                                    component="li"
                                                    sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}
                                                    >
                                                        {label}
                                                    </Typography>
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        label={categoryLabel}
                                                        sx={{
                                                            height: 20,
                                                            '& .MuiChip-label': {
                                                                px: 0.75,
                                                                fontSize: '0.62rem',
                                                            },
                                                        }}
                                                    />
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                    {(securityResolutionFailedRowsTruncated
                                        || securityResolutionFailedRowsTotal
                                            > securityResolutionFailedRows.length) && (
                                        <Typography variant="caption" color="text.secondary">
                                            {`Showing ${securityResolutionFailedRows.length} of `
                                                + `${securityResolutionFailedRowsTotal}.`}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Typography variant="body2">Security Resolution details are not available.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSecurityResolutionReviewOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={missingFactorReviewOpen}
                onClose={() => setMissingFactorReviewOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Missing Factor Data Review</DialogTitle>
                <DialogContent dividers>
                    {missingFactorReviewPayload ? (
                        <Box display="flex" flexDirection="column" gap={1.5}>
                            <Typography variant="body2" color="text.secondary">
                                {missingFactorReviewPayload?.message || 'Missing factor data details are available below.'}
                            </Typography>
                            <Box display="flex" gap={1} flexWrap="wrap">
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Model: ${missingFactorReviewPayload?.risk_source || 'Unknown'}`}
                                />
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Missing: ${Number(missingFactorReviewPayload?.missing_count || 0)}`}
                                />
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Unsupported estimates: ${Number(missingFactorReviewPayload?.imputed_count || 0)}`}
                                />
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Ignored: ${Number(missingFactorReviewPayload?.ignored_count || 0)}`}
                                />
                            </Box>
                            {missingFactorReviewPayload?.applied_policy === 'ignore' && (
                                <Typography variant="body2" color="text.secondary">
                                    Securities without verified factor data are excluded; estimates are not used.
                                </Typography>
                            )}
                            {missingFactorSecurityTotal > 0 ? (
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Affected Securities ({missingFactorSecurityTotal})
                                    </Typography>
                                    <Box display="flex" gap={0.75} flexWrap="wrap">
                                        {missingFactorSecurities.map((security) => (
                                            <Chip key={security} size="small" label={security} />
                                        ))}
                                        {missingFactorSecurityListTruncated && (
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={`+${Math.max(0, missingFactorSecurityTotal - missingFactorSecurities.length)} more`}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            ) : (
                                <Typography variant="body2">No affected securities were listed.</Typography>
                            )}
                        </Box>
                    ) : (
                        <Typography variant="body2">Missing factor notification details are not available.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMissingFactorReviewOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

NotificationBell.propTypes = {
    user: PropTypes.object,
    watchlistPublicationState: PropTypes.object,
    watchlistContextStatus: PropTypes.object,
    onReviewWatchlistContext: PropTypes.func,
};
